import { media } from "../../design/breakpoints";
import React, { useState, useEffect, useContext, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { GlobalState } from "../../GlobalState";
import { formatOfflineDuration } from "../utils/formatOfflineDuration";
import axios from "axios";

/* Identical to PumpStatusV2/V3/SolenoidBoxComponent's PbBlock */
const PbBlock = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: fit-content;
  min-width: 180px;
  max-width: 340px;
  margin: 0.5rem;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  display: flex;
  transition: all 0.3s ease;

  ${media.max480} {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0.3rem 0;
  }
`;

const AccentBar = styled.div`
  width: 5px;
  flex-shrink: 0;
  background: ${({ $isOnline }) => ($isOnline ? "#22C55E" : "#94A3B8")};
`;

const PbInner = styled.div`
  flex: 1;
  padding: 4px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const PbMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LocationName = styled.p`
  margin: 0;
  font-weight: 700;
  white-space: nowrap;
  font-size: 0.85rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;

  ${media.max768} {
    font-size: 0.75rem;
  }
`;

const StatusLine = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ $isOnline }) => ($isOnline ? "#16A34A" : "#9CA3AF")};

  ${media.max768} {
    font-size: 0.65rem;
  }
`;

const Badge = styled.span`
  font-size: 9.5px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $isOnline }) =>
    $isOnline ? "rgba(34,197,94,.12)" : "#F3F4F6"};
  color: ${({ $isOnline }) => ($isOnline ? "#15803D" : "#090a0e")};
`;

const BadgeStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.08);
  }
`;

const PlainDuration = styled.span`
  display: inline-block; /* Important */
  font-size: 10px;
  font-weight: 500;
  color: #d81111;
  white-space: nowrap;
  animation: ${pulse} 2s infinite ease-in-out;
  transform-origin: center;
`;



/* Robust boolean coercion — handles booleans, "1"/"0", "true"/"false", numbers.
   Boolean("false") is a real trap since any non-empty string is truthy in JS. */
function toBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "1" || v === "true" || v === "on";
  }
  return Boolean(value);
}

function useTouchTooltip(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let hideTimer;

    const onTouchStart = (e) => {
      e.preventDefault();
      el.classList.add("touch-active");
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => el.classList.remove("touch-active"), 2500);
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      clearTimeout(hideTimer);
    };
  }, [ref]);
}

export default function GatewayBoxComponent({ sensor, onStatusReport, hidden }) {
  const [gatewayData, setGatewayData] = useState(sensor || {});
  const state = useContext(GlobalState);
  const token = state?.token?.[0];

  const tooltipRef = useRef(null);
  useTouchTooltip(tooltipRef);


const fetchGatewayData = async () => {
  if (!sensor?.gateway_id || !token) return;

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/sensor/gateway?gateway_id=${sensor.gateway_id}`,
      { headers: { Authorization: state.token[0] } }
    );
    if (res.data?.data) {
      setGatewayData(res.data.data);
      if (onStatusReport) {
        onStatusReport(
          res.data.data.location_name || location_name || "Gateway",
          toBool(res.data.data.gateway_online)
        );
      }
    }
  } catch (err) {
    console.error("Error fetching gateway data:", err);
  }
};

  useEffect(() => {
    if (!sensor?.gateway_id) return;
    fetchGatewayData();
    const interval = setInterval(fetchGatewayData, 5000);
    return () => clearInterval(interval);
  }, [sensor?.gateway_id, token]);

  // FIX #4: when hidden, render nothing visible but still mount to report status
  if (hidden) return null;

  const { gateway_online, location_name, updatedAt } = gatewayData;
  const isOnline = toBool(gateway_online);

  const MAX_LENGTH = 15;
  const truncatedLocationName =
    location_name?.length > MAX_LENGTH
      ? location_name.slice(0, MAX_LENGTH)
      : location_name;

  if (!location_name) {
    return (
      <PbBlock>
        <AccentBar $isOnline={false} />
        <PbInner>
          <StatusLine $isOnline={false}>Loading...</StatusLine>
        </PbInner>
      </PbBlock>
    );
  }

  return (
    <PbBlock>
      <AccentBar $isOnline={isOnline} />

      <PbInner>
        <TopRow>
          <PbMeta>
            <LocationName>{truncatedLocationName}</LocationName>
          </PbMeta>

          <BadgeStack>
            <Badge $isOnline={isOnline}>{isOnline ? "online" : "offline"}</Badge>

            {!isOnline && (
              <PlainDuration>
                {formatOfflineDuration(updatedAt)}
              </PlainDuration>
            )}
          </BadgeStack>
        </TopRow>
      </PbInner>
    </PbBlock>
  );
}
